import {
  FlexBox,
  JoonUIColor,
  Loader,
  SPACING,
  Typography,
} from "@joonapp/web-shared"
import type { BaseMessage } from "@sendbird/chat/message"
import { useRef, Fragment } from "react"

import MessageItem from "./MessageItem"
import MessagesListScrollButton from "./MessagesListScrollButton"
import { useSendbird } from "./SBProvider"
import sb from "./sendbird"
import { areMessagesInSameMinute, formatDate } from "../../util/dates"

const MessageList = () => {
  const { messages, loadPreviousMessages, isLoadingMessages, hasMore } =
    useSendbird()
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const currentUserId = sb.currentUser?.userId

  const onScroll = async (e: React.UIEvent<HTMLDivElement>) => {
    const element = e.target as HTMLDivElement
    const hasOverflow = element.scrollHeight > element.clientHeight
    if (isLoadingMessages || !hasMore || !hasOverflow) return

    if (element.scrollTop < 100) {
      const previousHeight = element.scrollHeight
      await loadPreviousMessages()
      const heightDiff = element.scrollHeight - previousHeight
      element.scrollTop = heightDiff
    }
  }

  return (
    <div
      ref={scrollContainerRef}
      id="messages-container"
      onScroll={onScroll}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: SPACING.space2,
        position: "relative",
        height: "100%",
        width: "100%",
        overflowY: "auto",
        padding: SPACING.space4,
      }}
    >
      {isLoadingMessages && (
        <FlexBox justify="center" style={{ padding: SPACING.space4 }}>
          <Loader size={20} color={JoonUIColor.text.primary} thickness={2} />
        </FlexBox>
      )}

      {messages.map(
        // @ts-ignore
        (message: BaseMessage & { sender: User }, index: number) => {
          const previousMessage = index > 0 ? messages[index - 1] : null
          const currentDate = new Date(message.createdAt)
          const previousDate = previousMessage
            ? new Date(previousMessage.createdAt)
            : null

          const showDateSeparator =
            !previousMessage ||
            currentDate.toDateString() !== previousDate?.toDateString()

          const showTimestampAndAvatar =
            !previousMessage ||
            !areMessagesInSameMinute(message, previousMessage)

          // Add check for admin message
          if (message.messageType === "admin") {
            return (
              <Fragment key={message.messageId}>
                {showDateSeparator && (
                  <Typography
                    variant="bodyXSmall"
                    style={{ textAlign: "center", marginTop: SPACING.space2 }}
                  >
                    {formatDate(currentDate)}
                  </Typography>
                )}
                <Typography
                  variant="bodyXSmall"
                  style={{
                    textAlign: "center",
                    color: JoonUIColor.text.secondary,
                    padding: SPACING.space2,
                    lineHeight: "1rem",
                    maxWidth: "500px",
                    margin: "0 auto",
                  }}
                >
                  {message.message}
                </Typography>
              </Fragment>
            )
          }

          return (
            <Fragment key={message.messageId}>
              {showDateSeparator && (
                <Typography
                  variant="bodyXSmall"
                  style={{ textAlign: "center", marginTop: SPACING.space2 }}
                >
                  {formatDate(currentDate)}
                </Typography>
              )}
              <MessageItem
                message={message}
                isMyMessage={message.sender?.userId === currentUserId}
                showTimestamp={showTimestampAndAvatar}
                showAvatar={showTimestampAndAvatar}
              />
            </Fragment>
          )
        }
      )}

      <MessagesListScrollButton containerRef={scrollContainerRef} />
    </div>
  )
}

export default MessageList
